"use client";

import React from "react";
import { Briefcase, Cpu, Factory, Globe2, HardHat, Users } from "lucide-react";
import Reveal from "@/components/common/Reveal";
import { useLanguage } from "@/hooks/useLanguage";
import { pick } from "@/lib/i18n";
interface PersonaCard {
  title: string;
  description: string;
}
interface WhoWeServeData {
  eyebrow: string;
  heading: string;
  lead: string;
  cards: [PersonaCard, PersonaCard, PersonaCard, PersonaCard, PersonaCard, PersonaCard];
}
const copy: Record<"en" | "zh" | "es" | "de" | "fr" | "pt", WhoWeServeData> = {
  en: {
    eyebrow: "Who We Serve",
    heading: "Who We Work With",
    lead: "MutualFilter supplies industrial filtration and separation mesh to buyers and technical teams across North America, Europe and international markets.",
    cards: [{
      title: "Industrial buyers and purchasing managers",
      description: "Clear specifications and one point of contact for sourcing filtration mesh."
    }, {
      title: "Filter and filtration-equipment manufacturers",
      description: "Mesh and components matched to your element design and media requirements."
    }, {
      title: "OEM buyers",
      description: "Custom specifications and drawings supported for repeat OEM programmes."
    }, {
      title: "Distributors and importers",
      description: "A focused catalogue of four product categories for resale and stocking."
    }, {
      title: "Engineers",
      description: "Technical guidance on weave, aperture, wire diameter and material selection."
    }, {
      title: "Project teams and end users",
      description: "Support from specification through to supply coordination."
    }]
  },
  zh: {
    eyebrow: "服务对象",
    heading: "我们的合作伙伴",
    lead: "MutualFilter 为北美、欧洲及全球市场的采购商与技术团队提供工业过滤与分离丝网及组件支持。",
    cards: [{
      title: "工业买家与采购经理",
      description: "明确的技术规格与统一对接窗口，简化过滤丝网采购流程。"
    }, {
      title: "过滤器与过滤设备制造商",
      description: "精准匹配滤芯结构设计与过滤介质要求的丝网与定制组件。"
    }, {
      title: "OEM 采购团队",
      description: "支持定制图纸与规格，满足长期批量的 OEM 配套供应需求。"
    }, {
      title: "分销商与进出口商",
      description: "涵盖四大核心产品分类的聚焦产品目录，便于转售与备库。"
    }, {
      title: "技术工程师",
      description: "在编织形式、孔径、丝径及材质选择方面提供严谨的技术指导。"
    }, {
      title: "工程项目团队与终端用户",
      description: "提供从前期规格选型到后续供应协调的全流程支持。"
    }]
  },
  es: {
    eyebrow: "A quién servimos",
    heading: "Con quién trabajamos",
    lead: "MutualFilter suministra malla para filtración y separación industrial a compradores y equipos técnicos en América del Norte, Europa y mercados internacionales.",
    cards: [{
      title: "Compradores industriales y directores de compras",
      description: "Especificaciones claras y un único interlocutor para la compra de malla filtrante."
    }, {
      title: "Fabricantes de filtros y equipos de filtración",
      description: "Malla y componentes adaptados al diseño de sus elementos y requisitos de medio."
    }, {
      title: "Compradores OEM",
      description: "Soporte para planos y especificaciones a medida en programas de suministro recurrente."
    }, {
      title: "Distribuidores e importadores",
      description: "Un catálogo enfocado en cuatro categorías de producto para distribución y stock."
    }, {
      title: "Ingenieros",
      description: "Orientación técnica en tipo de tejido, abertura, diámetro de alambre y selección de aleación."
    }, {
      title: "Equipos de proyecto y usuarios finales",
      description: "Acompañamiento continuo desde la definición técnica hasta la coordinación del suministro."
    }]
  },
  de: {
    eyebrow: "Zielgruppen",
    heading: "Mit wem wir arbeiten",
    lead: "MutualFilter liefert Drahtgewebe zur industriellen Filtration und Trennung an Einkäufer und Technikteams in Nordamerika, Europa und weltweiten Märkten.",
    cards: [{
      title: "Industrielle Einkäufer und Beschaffungsleiter",
      description: "Eindeutige Spezifikationen und ein zentraler Ansprechpartner für die Gewebebeschaffung."
    }, {
      title: "Hersteller von Filtern und Filtrationsanlagen",
      description: "Gewebe und Komponenten exakt abgestimmt auf Ihre Filterelemente und Medienanforderungen."
    }, {
      title: "OEM-Einkäufer",
      description: "Unterstützung kundenspezifischer Zeichnungen und Spezifikationen für Serienprogramme."
    }, {
      title: "Distributoren und Importeure",
      description: "Fokussiertes Sortiment aus vier Produktkategorien für Wiederverkauf und Lagerhaltung."
    }, {
      title: "Ingenieure und Konstrukteure",
      description: "Technische Beratung zu Webart, Maschenweite, Drahtdurchmesser und Werkstoffauswahl."
    }, {
      title: "Projektteams und Endanwender",
      description: "Begleitung von der Auslegung bis zur logistischen Lieferkoordination."
    }]
  },
  fr: {
    eyebrow: "Nos clients",
    heading: "Avec qui nous travaillons",
    lead: "MutualFilter fournit des toiles métalliques de filtration et de séparation industrielle aux acheteurs et équipes techniques en Amérique du Nord, Europe et marchés internationaux.",
    cards: [{
      title: "Acheteurs industriels et responsables approvisionnement",
      description: "Spécifications précises et point de contact dédié pour l'approvisionnement en toiles filtrantes."
    }, {
      title: "Fabricants de filtres et d'équipements de filtration",
      description: "Toiles et composants adaptés à la conception de vos éléments et aux exigences de filtration."
    }, {
      title: "Acheteurs OEM",
      description: "Prise en charge de plans et spécifications sur mesure pour les programmes de fabrication en série."
    }, {
      title: "Distributeurs et importateurs",
      description: "Un catalogue ciblé de quatre catégories de produits pour le stockage et la revente."
    }, {
      title: "Ingénieurs",
      description: "Conseils techniques sur le type de tissage, l'ouverture de maille, le diamètre de fil et le choix des nuances."
    }, {
      title: "Équipes projet et utilisateurs finaux",
      description: "Accompagnement continu, de la spécification technique jusqu'à la coordination des livraisons."
    }]
  },
  pt: {
    eyebrow: "A quem atendemos",
    heading: "Com quem trabalhamos",
    lead: "A MutualFilter fornece telas para filtração e separação industrial a compradores e equipes técnicas na América do Norte, Europa e mercados internacionais.",
    cards: [{
      title: "Compradores industriais e gestores de suprimentos",
      description: "Especificações claras e um ponto focal dedicado para a compra de telas de filtração."
    }, {
      title: "Fabricantes de filtros e equipamentos de filtração",
      description: "Telas e componentes alinhados ao projeto do elemento filtrante e aos requisitos do processo."
    }, {
      title: "Compradores OEM",
      description: "Apoio a desenhos técnicos e especificações sob medida para programas recorrentes de OEM."
    }, {
      title: "Distribuidores e importadores",
      description: "Catálogo focado em quatro categorias de produtos para revenda e formação de estoque."
    }, {
      title: "Engenheiros",
      description: "Orientação técnica sobre padrão de tecelagem, abertura de malha, diâmetro do fio e liga metálica."
    }, {
      title: "Equipes de projetos e usuários finais",
      description: "Suporte contínuo, desde a definição técnica inicial até a coordenação do fornecimento."
    }]
  }
};
const cardIcons = [Briefcase, Factory, Cpu, Globe2, HardHat, Users];
export default function WhoWeServe() {
  const {
    lang
  } = useLanguage();
  const content = pick(lang, copy);
  return <section id="whoweserve" data-nav="light" className="bg-muted/40 text-foreground border-b border-border mf-section">
      <div className="mf-container">
        {/* Section Header */}
        <Reveal>
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 font-mono mf-caption uppercase tracking-wider text-primary font-semibold mb-3">
              <span className="h-1.5 w-1.5 rounded-full bg-primary" />
              <span>{content.eyebrow}</span>
            </div>

            <h2 className="mf-h2 text-foreground">
              {content.heading}
            </h2>

            <p className="mt-4 mf-lead text-muted-foreground leading-relaxed">
              {content.lead}
            </p>
          </div>
        </Reveal>

        {/* 6-Card Grid: 3-col on lg, 2-col on sm/md, 1-col on mobile */}
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {content.cards.map((card, idx) => {
          const Icon = cardIcons[idx];
          return <Reveal key={idx} delay={Math.min(idx * 60, 300)}>
                <div className="rounded-lg border border-border bg-card p-6 flex flex-col justify-between h-full mf-hover-lift">
                  <div>
                    <div className="size-10 rounded-md border border-border bg-muted flex items-center justify-center text-primary mb-4 shrink-0">
                      <Icon className="size-5" />
                    </div>
                    <h3 className="mf-h3 text-card-foreground">
                      {card.title}
                    </h3>
                    <p className="mt-2.5 text-sm sm:text-base text-muted-foreground leading-relaxed">
                      {card.description}
                    </p>
                  </div>
                </div>
              </Reveal>;
        })}
        </div>
      </div>
    </section>;
}